/**
 * AppHeader — En-tête unique de l'application (format de référence : image 8).
 *
 * Toutes les surfaces où un utilisateur est connecté (espaces internes ET
 * espaces administré) utilisent CE header, pour une présentation cohérente :
 *
 *   [LogoMark]  MAIRIE DE                      {prénom} {nom} · {fonction}   [← Tableau de bord] [☀/�MOON]
 *               {nom commune} · {module}
 *
 * - La « fonction » = libellé rôle (+ service si pertinent), ex. « Agent — Communication ».
 * - Le bouton de retour est TOUJOURS « Tableau de bord » (jamais « Mon espace » / « Retour à l'accueil »).
 * - `accent` colore le LogoMark + le halo selon le module courant.
 */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Landmark } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { LogoMark, SectionEyebrow, type LogoMarkAccent } from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { MessagesMenu } from '@/components/MessagesMenu';

/** Libellés de fonction — source unique de vérité, partagée par tout l'app. */
export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super admin',
  admin: 'Maire / adjoint',
  manager: 'Responsable',
  agent: 'Agent',
  administre: 'Administré',
};
export const SERVICE_LABELS: Record<string, string> = {
  service_technique: 'Technique',
  scolaire_cantine: 'Scolaire / cantine',
  petite_enfance: 'Petite enfance',
  urbanisme: 'Urbanisme',
  associations: 'Associations',
  grc: 'Communication',
  rh: 'Ressources humaines',
  comptable: 'Comptabilité',
};

/** Construit la fonction lisible affichée à droite du nom. */
export function fonctionLabel(role?: string | null, service?: string | null): string {
  const r = ROLE_LABELS[role ?? ''] ?? role ?? '';
  if (service && SERVICE_LABELS[service]) return `${r} — ${SERVICE_LABELS[service]}`;
  return r;
}

export function AppHeader({
  module,
  accent = 'tech',
  dashboardTo = '/dashboard',
  actions,
}: {
  /** Libellé du module courant affiché après le nom de la commune (ex. « Service technique »). */
  module?: string;
  /** Couleur d'accent du LogoMark + halo. */
  accent?: LogoMarkAccent;
  /** Destination du bouton retour (par défaut le dashboard). */
  dashboardTo?: string;
  /** Actions contextuelles optionnelles (ex. bouton Recharger), rendues avant le bouton Dashboard. */
  actions?: ReactNode;
}) {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();

  // RGAA 2.4.2 — titre de page unique basé sur le module courant.
  usePageTitle(module);

  const topbarBg = {
    backgroundImage:
      `radial-gradient(220px 80px at 10% 50%, oklch(var(--c-${accent}) / 0.22), transparent 70%), ` +
      'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
  };

  return (
    <header
      className="sticky top-0 z-30 flex items-center gap-3 border-b border-border px-4 py-3 backdrop-blur-glass sm:px-6"
      style={topbarBg}
    >
      <LogoMark icon={Landmark} accent={accent} size="md" aria-label="Mairie" />
      <div className="min-w-0 flex-1">
        <SectionEyebrow>Mairie de</SectionEyebrow>
        <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
          {tenant?.nom ?? '—'}
          {module && <span className="ml-1 font-normal text-ink-muted">· {module}</span>}
        </div>
      </div>
      <div className="hidden sm:block text-right text-xs text-ink-muted">
        {user?.prenom} {user?.nom}
        {user && <span className="ml-1 text-ink-subtle">· {fonctionLabel(user.role, user.service)}</span>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
      {/* Refonte UX, chantier 2 (C1) — Messages sur tous les écrans : notifications,
          messagerie interne, diffusions. Remplace la cloche qui n'existait que sur l'accueil. */}
      <MessagesMenu />
      <button
        type="button"
        onClick={() => navigate(dashboardTo)}
        className="group inline-flex h-10 items-center gap-1 rounded-r-md border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink transition-colors hover:bg-[oklch(var(--c-accent)/0.10)] hover:border-[oklch(var(--c-accent)/0.40)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(var(--c-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        style={{ ['--c-accent' as string]: `var(--c-${accent})` }}
        aria-label="Retour au tableau de bord"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
        <span className="hidden sm:inline">Tableau de bord</span>
      </button>
      <ThemeToggle />
    </header>
  );
}
